import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCostReport } from "actions/Monitoring";
import { getCompaniesList } from "actions/Catalogs";
import { useTranslation } from "react-i18next";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel, } from "@tanstack/react-table";
import AppoinmentColumns from "utils/columns/CostReport";
import DataTable from "components/DataTable";
import Loader from "components/UI/Loader";
import { notify } from "utils";
import { crearExcel } from "utils/Excel";
import { columnsCostReportExcel } from "utils/columns/columnsExcel";
import { CiSearch } from "react-icons/ci";
import { FaFileDownload } from "react-icons/fa";

const CostReport = ({ view }) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const { currentProject } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const [t] = useTranslation("monitoring");
  const [companyList, setCompanyList] = useState([]);
  const [filter, setFilter] = useState({
    idCompany: null,
    startDate: "",
    endDate: "",
  });

  const getParams = useCallback(() => {
    return Object.entries(filter)
      .filter(([_, value]) => value !== null && value !== undefined && value !== "" && value !== "0")
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join("&");
  }, [filter]);

  const handleSearch = () => {
    if (currentProject && currentProject?.id) {
      getData();
    }
  };
  
  const handleCreationExcel = async () => {
    try {
      if (data.length <= 0) {
        notify("warning", "No hay datos", 600)
        return;
      }

      const fileName = `ReporteDeCostos.xlsx`;
      const sheetName = "Reporte";
      const formattedData = data.map(info => ({
        ...info,
        subTotal: info.subTotal != null ? `$${Number(info.subTotal).toLocaleString()}` : "",
        dayCost: info.dayCost != null ? `$${Number(info.dayCost).toLocaleString()}` : "",
        iva: info.iva != null ? `$${Number(info.iva).toLocaleString()}` : "",
        total: info.total != null ? `$${Number(info.total).toLocaleString()}` : ""
      }));

      await crearExcel(fileName, sheetName, formattedData, columnsCostReportExcel(t));
    } catch (error) {
      console.error("Error al crear el archivo Excel:", error);
    }
  };

  const getData = async (params = getParams()) => {
    if (!validateDates(filter.startDate, filter.endDate)) return;
    setIsLoading(true)
    const response = await getCostReport(params);
    setIsLoading(false)
    setData(response ?? []);
  }

  const parseLocalDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-');
    return new Date(y, m - 1, d); // fecha local, sin hora
  };

  const validateDates = (dateInit, dateEnd) => {
    const initDate = parseLocalDate(dateInit);
    const endDate = parseLocalDate(dateEnd);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (initDate > endDate) {
      notify("warning", t("alerts.error_invalid_date_range"), 4000);
      return false;
    }

    if (initDate > today || endDate > today) {
      notify("warning", t("alerts.error_future_date"), 4000);
      return false;
    }

    return true;
  };


  useEffect(() => {
    if (!currentProject?.id) return;

    const loadInitialData = async () => {
      try {
        const companyList = await dispatch(
          getCompaniesList(true)
        );

        setCompanyList(companyList ?? []);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [currentProject, dispatch]);

  const table = useReactTable({
    data,
    columns: AppoinmentColumns(t),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

    return (
      <div className="bg-[#f4f3fb] min-h-screen p-6">
        <div className="bg-black flex flex-row justify-between text-white px-6 py-4 rounded-t-xl">
          <h1 className="text-lg font-bold">{t("costReport.title")}</h1>
          <div className="flex flex-wrap justify-end items-center gap-4">
             <div className="flex flex-col">
              <label className="text-sm font-medium">{t("costReport.company")}</label>
              <select
                className="border text-black border-gray-300 rounded px-2 py-1 text-sm min-w-[200px]"
                name="idCompany"
                value={filter.idCompany || ''}
                onChange={handleChange}
                disabled={isLoading}
                >
                <option value={0}>Selecciona una opción</option>
                {
                  companyList.map((item, index) => (
                    <option value={item.id} key={index}> {item.name} </option>
                  ))
                }
              </select>
             </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium">{t("costReport.startDate")}</label>
              <input
                className="text-sm text-black border border-gray-300 rounded px-2 py-1"
                name="startDate"
                type="date"
                value={filter.startDate}
                onChange={handleChange}
              />
            </div>
            <div className="flex flex-col">
              <label className="text-sm font-medium">{t("costReport.endDate")}</label>
              <input
                className="text-sm text-black border border-gray-300 rounded px-2 py-1"
                name="endDate"
                type="date"
                value={filter.endDate}
                onChange={handleChange}
              />
            </div>
            
            <button onClick={() => handleSearch()} className="h-8 w-8 bg-white border border-gray-300 py-1 rounded-full hover:bg-gray-100 shadow text-black self-end">
              <CiSearch className="text-2xl self-center justify-self-center" />
            </button>
            <button onClick={() => handleCreationExcel()} className="h-8 w-8 bg-white border border-gray-300 py-1 rounded-full hover:bg-gray-100 shadow text-black self-end">
              <FaFileDownload className="text-2xl self-center justify-self-center" />
            </button>


          </div> 
        </div>

        {
        isLoading ? (
          <Loader />
        ) : (
          <div className="lg:h-[75vh] pt-2">
            <DataTable table={table} w="100%" />
          </div>
        )
      }
      </div>
    );
};

export default CostReport;